import { useState, Fragment, useEffect } from 'react';

import { getTutorials } from '../../../../store/tutorials';
import { useAppSelector } from '../../../../store/configureStore';

import { tutorialConstants } from '../../../../utils/constants';
import { MTCoverData, MTInspectionReportData, PDFImageProps, PDFVIPageProps, VTEquipmentAnomalyData } from '../../../../utils/models';

import Button from '../../../themed/button';
import Elevation from '../../../themed/instruction-modals/elevation';
import ETAnomalySummary from '../et/etAnomalySummary';
import Modal from '../../../themed/modal';
import MTCoverForm from '../../../forms/mt/mtCoverForm';
import MTDrawingSheetForm from '../../../forms/mt/mtDrawingSheetForm';
import MTInspectionDataForm from '../../../forms/mt/mtInspectionDataForm';
import MTPhotographicRecordForm from '../../../forms/mt/mtPhotographicRecord';
import MTReport from './';
import MTSummaryForm from '../../../forms/mt/mtSummaryForm';
import Zippy from '../../../themed/zippy';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

const CreateMTReport = () => {
	const [anomalyData, setAnomalyData] = useState<VTEquipmentAnomalyData[]>([]);
	const [mtCoverData, setMTCoverData] = useState<MTCoverData>();
	const [viPageData, setVIPageData] = useState<PDFVIPageProps>();
	const [drawingData, setDrawingData] = useState<PDFImageProps[]>([]);
	const [photographicData, setPhotographicData] = useState<PDFImageProps[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [mtReportData, setMTReportData] = useState<MTInspectionReportData | null>();

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const getReportDetails = () => {
		return {
			anomalyData,
			coverSheetData: mtCoverData,
			viPageData,
			mtReportData,
			projectDetails,
			drawingData,
			photographicData,
		};
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setAnomalyData(cachedReport.anomalyData);
			setMTCoverData(cachedReport.coverSheetData);
			setVIPageData(cachedReport.viPageData);
			setMTReportData(cachedReport.mtReportData);
			setDrawingData(cachedReport.drawingData);
			setPhotographicData(cachedReport.photographicData);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<MTReport {...reportDetails} />
			</Modal>

			<Zippy headerTitle="Cover Sheet" isValid={!!mtCoverData}>
				<MTCoverForm onProceed={setMTCoverData} />
			</Zippy>

			<Zippy headerTitle="Summary Sheet" isValid={!!viPageData}>
				<MTSummaryForm onProceed={setVIPageData} />
			</Zippy>

			<Zippy headerTitle="Drawing Sheet" isValid={drawingData.length > 0}>
				<MTDrawingSheetForm onProceed={setDrawingData} />
			</Zippy>

			<Zippy
				headerTitle="Magnetic Particle Testing Report"
				isValid={!!mtReportData}
			>
				<MTInspectionDataForm onProceed={setMTReportData} />
			</Zippy>

			<Zippy headerTitle="Anomaly Data" isValid={anomalyData.length > 0}>
				<ETAnomalySummary
					anomalyData={anomalyData}
					onDataChange={setAnomalyData}
				/>
			</Zippy>

			<Zippy
				headerTitle="Photographic Report"
				isValid={photographicData.length > 0}
			>
				<MTPhotographicRecordForm onProceed={setPhotographicData} />
			</Zippy>

			<div className="d-flex justify-content-end">
				<Elevation
					isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}
				>
					<Button
						label="Save and Preview Report"
						disabled={error}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CreateMTReport;
